import axios, { AxiosInstance, AxiosError, InternalAxiosRequestConfig } from 'axios';
import { ErrorResponseType, HttpStatus } from '../entities/common';
import { PAGES } from '../utils/link';

const API_BASE_URL =
  process.env.REACT_APP_API_URL || 'https://primeoasis.nolim.app';

const TOKEN_KEY = 'access_token';

export const api: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

api.interceptors.response.use(
  undefined,
  (error: AxiosError<ErrorResponseType>) => {
    if ((error.config as any)?.skipRedirects) {
      return Promise.reject(error);
    }

    const currentPath = window.location.pathname;
    const isOnErrorPage = [
      PAGES.BAD_REQUEST,
      PAGES.UNAUTHORIZED,
      PAGES.NOT_ACCESS,
      PAGES.NOT_FOUND,
      PAGES.LINK_GONE,
      PAGES.SERVER_ERROR,
    ].includes(currentPath);

    const isOnUserFormPage = currentPath.includes('/user/create') || currentPath.includes('/user/update');

    if (error.response?.status === HttpStatus.BadRequest) {
      if (!isOnErrorPage && !isOnUserFormPage) {
        window.location.replace(PAGES.BAD_REQUEST);
      }
      if (isOnUserFormPage) {
        return Promise.reject(error);
      }
    } else if (error.response?.status === HttpStatus.Unauthorized) {
      const isOnLoginPage = currentPath === PAGES.AUTH_LOGIN;

      if (isOnLoginPage) {
        return Promise.reject(error);
      }

      localStorage.removeItem(TOKEN_KEY);

      if (!isOnErrorPage) {
        window.location.replace(PAGES.AUTH_LOGIN);
      }
    } else if (error.response?.status === HttpStatus.Forbidden) {
      const errorDetail = error.response?.data?.detail || error.response?.data?.message;
      if (errorDetail === 'Active subscription required') {
        if (currentPath !== PAGES.SUBSCRIPTION) {
          window.location.replace(PAGES.SUBSCRIPTION);
        }
      } else if (!isOnErrorPage) {
        window.location.replace(PAGES.NOT_ACCESS);
      }
    } else if (error.response?.status === HttpStatus.NotFound) {
      if (!isOnErrorPage && !isOnUserFormPage) {
        window.location.replace(PAGES.NOT_FOUND);
      }
      if (isOnUserFormPage) {
        return Promise.reject(error);
      }
    } else if (error.response?.status === HttpStatus.Gone) {
      if (!isOnErrorPage) {
        window.location.replace(PAGES.LINK_GONE);
      }
    } else if (
      (error.response?.status || 0) >= HttpStatus.InternalServerError
    ) {
      if (!isOnErrorPage) {
        window.location.replace(PAGES.SERVER_ERROR);
      }
    } else if (
      error.response?.status === HttpStatus.ManyRequests ||
      error.response?.status === HttpStatus.LargeRequest
    ) {
      return Promise.reject(error.response?.data?.message);
    } else if (error.response?.status === HttpStatus.UnprocessableEntity) {
      if (error.response?.data?.errors) {
        const { message, errors } = error.response.data;

        if (Object.keys(errors).length) {
          return Promise.reject(errors);
        }

        return Promise.reject(message);
      }
    } else if (error.response?.status === HttpStatus.Conflict) {
      if (error.response?.data) {
        return Promise.reject(error.response.data);
      }
    }

    return Promise.reject(error?.response?.data?.message || error.toString());
  },
);

export default api;
